/**
 * ITEM 1 — which OFFLINE game a solo join is opening.
 *
 * A saved game used to be "the" game for its game id: starting a second chess game could only
 * resume the first or overwrite it, and picking new settings silently dropped the player back
 * into the old game. Saves are now independent rows, so:
 *
 *  - a game TILE always starts a NEW game, ADDED alongside whatever is already saved;
 *  - the «ادامه بازی» strip resumes ONE named save, with the settings it was started with.
 *
 * This module carries the second case from the lobby to the solo connector, which is not a
 * component and cannot read React state. One shared place means every offline game is covered —
 * including any added later — instead of eight containers each having to remember.
 *
 * CONSUMED, not merely read: only the first reader sees it, so a reconnect resumes the game now
 * in progress rather than re-opening an older save, and a later unrelated tap starts fresh.
 */
let pendingResumeId: string | null = null;
let pendingResumeSkipsStartScreen = false;

/** The lobby is opening a specific SAVED game. */
export function setResumeSolo(id: string): void {
  pendingResumeId = id;
  pendingResumeSkipsStartScreen = true;
}

/** Read and clear: the saved game this join should resume, if any. */
export function takeResumeSolo(): string | null {
  const id = pendingResumeId;
  pendingResumeId = null;
  return id;
}

/**
 * Read and clear: should a board game's start screen stand aside?
 *
 * Separate from {@link takeResumeSolo} because the two are consumed by different code at
 * different moments — the START SCREEN mounts first (and must skip itself), the CONNECTOR joins
 * after. Sharing one flag would mean whichever ran first swallowed it.
 */
export function takeResumeIntent(): boolean {
  const was = pendingResumeSkipsStartScreen;
  pendingResumeSkipsStartScreen = false;
  return was;
}

/**
 * …AND THE ANSWER THE OTHER WAY: THE SAVE A JOIN NAMED IS GONE. The server retired it (made under
 * rules the game no longer plays — deleted, recorded as nothing). The connector that heard it is not
 * a component and cannot reach the session, so it says so here, and the session re-reads
 * «ادامه بازی» at once: the card must be gone before the player is back in the lobby to tap it
 * (the lobby's own re-read on mount comes a request later).
 */
const savedGameRetiredListeners = new Set<() => void>();

/** Listen for a retired save (the session, for its «ادامه بازی» list). Returns the unsubscribe. */
export function onSavedGameRetired(listener: () => void): () => void {
  savedGameRetiredListeners.add(listener);
  return () => {
    savedGameRetiredListeners.delete(listener);
  };
}

/** The solo connector heard `SAVED_GAME_RETIRED_MESSAGE`, at the «ادامه بازی» join or a re-join. */
export function noteSavedGameRetired(): void {
  for (const listener of [...savedGameRetiredListeners]) {
    try {
      listener();
    } catch (error) {
      // A listener that throws must not stop the others, nor the refusal the caller is handling.
      console.error("[solo] a retired-save listener failed", error);
    }
  }
}

/** TEST SEAM — clear any leftover intent between cases. */
export function __resetSoloIntent(): void {
  pendingResumeId = null;
  pendingResumeSkipsStartScreen = false;
}
